import React, { useMemo } from 'react';
import {
  Header,
  LeftSection,
  ProductInfo,
  ProductionCard,
  ProductionStats,
  ProductionValue,
  StatBlock,
  StyledDescText,
} from './ProductionOrderV2.styles';
import { AllieCubeOutlineIcon, AllieDotIcon } from '../../../styling/icons';
import { OrderStatus } from './components/ToolbarButtons';
import { POProgressBar } from './ProductionOrder.styles';
import { AllieText } from '../../../components/Text';
import { AllieChip } from '../../../components/Chip';
import { AllieEllipsedText } from '../EllipsedText';
import { MulticolorProgressBar } from '../../../components/ProgressIndicators/MulticolorBar';
import { getColorBasedOnThreshold } from '../../../common/utils';
import { AllieColors } from '../../../common/color.types';

interface ProductionProgressProps {
  op: string;
  status: string;
  product: string;
  currentProduction: string;
  estimatedProduction: string;
  progress: number;
  labels: {
    status: string;
    statusDone: string;
    statusPaused: string;
    shiftProduction: string;
    estimatedProduction: string;
    progress: string;
  };
  redThreshold?: number;
  greenThreshold?: number;
}

export const ProductionOrderTvV2: React.FC<ProductionProgressProps> = ({ 
  op,
  status,
  product,
  currentProduction,
  estimatedProduction,
  labels,
  progress,
  redThreshold,
  greenThreshold,
}) => {

  const mapProductionStatusChipBorder: Record<string, string> = {
    in_progress: '1.5px solid var(--allie-color-primary-60)',
    done: '1.5px solid var(--allie-color-neutral-60)',
    paused: '1.5px solid var(--allie-color-neutral-60)',
  };
  const mapProductionStatusChipColor: Record<string, string> = {
    in_progress: 'var(--allie-color-primary-70)',
    done: 'var(--allie-color-neutral-70)',
    paused: 'var(--allie-color-neutral-70)',
  };
  const mapStatusLabel: Record<string, string> = {
    in_progress: labels.status,
    done: labels.statusDone,
    paused: labels.statusPaused,
  };

  const progressColor = useMemo(() => (
    redThreshold || greenThreshold
      ? (getColorBasedOnThreshold(
        (progress ?? 0) * 100,
        redThreshold,
        greenThreshold
      ) as AllieColors) : 'primary-60'
  ), [progress, redThreshold, greenThreshold]);

  return (
    <ProductionCard>
      <Header>
        <LeftSection>
          <AllieText type='h3' weight={600}>{op}</AllieText>
          {status && (
            <AllieChip
              style={{
                height: 28,
                cursor: 'default',
                border: mapProductionStatusChipBorder[status],
                borderRadius: '50px',
                padding: '2px 6px',
                margin: 0,
                gap: '4px',
                fontSize: '21px',
              }}
            >
              {status === OrderStatus.InProgress && (
                <div style={{ alignSelf: 'end' }}><AllieDotIcon /></div>
              )}
              <StyledDescText
                type="body2"
                style={{
                  color: mapProductionStatusChipColor[status],
                }}
                weight={500}
              >
                {mapStatusLabel[status]}
              </StyledDescText>
            </AllieChip>
          )}
        </LeftSection>
        <ProductInfo>
          <AllieCubeOutlineIcon />
          <AllieText type='h5' weight={500}>{product}</AllieText>
        </ProductInfo>
      </Header>

      <ProductionStats>
        <StatBlock>
          <AllieText type='h5' weight={500} color='neutral-60'>{labels.shiftProduction}</AllieText>
          <ProductionValue>{currentProduction}</ProductionValue>
        </StatBlock>
        <StatBlock>
          <AllieText
            type='h5'
            weight={500}
            color='neutral-60'
          >
            {labels.estimatedProduction}
          </AllieText>
          <ProductionValue>{estimatedProduction}</ProductionValue>
        </StatBlock>
      </ProductionStats>

      <POProgressBar isTv style={{ position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 10px',
            height: '100%',
            position: 'absolute',
            width: '100%',
            zIndex: 1,
          }}
        >
          <AllieEllipsedText
            textProps={{
              type: 'body2',
              color: `${progress && progress <= 0.65 ? 'black-90' : 'white'}`,
              weight: 500,
            }}
            value={
              progress !== undefined && progress > 0.1 && labels.progress
                ? `${labels.progress}: ${Math.round((progress || 0) * 100)}%`
                : ''
            }
            triggerId={'planned-production'}
            maxWidth={'auto'}
          />
        </div>
        <MulticolorProgressBar
          values={[ { color: progressColor, value: (progress ?? 0) * 100 } ]}
          height={'100%'}
          borderRadius={100}
          weightRemainingText={600}
          remainingText={
            progress !== undefined && progress <= 0.1
              ? `${Math.round((progress || 0) * 100)}% ${labels.progress}`
              : undefined
          }
        />
      </POProgressBar>
    </ProductionCard>
  );
};
